import { VideoPresets, type RoomOptions } from "livekit-client";

/**
 * The camera resolution every participant captures and publishes.
 *
 * A therapy session is a talking head on a still background, and 360p carries
 * that legibly at a fraction of 720p's bandwidth — which is most of what a
 * meeting costs. If it ever reads as too soft on a full session, 540p is the
 * next step: change it here and nowhere else.
 */
export const MEETING_CAMERA_PRESET = VideoPresets.h360;

/**
 * How every AlchelyMeeting room is joined — therapist, patient and scheduler
 * rooms alike, so the three can never drift apart.
 *
 * `adaptiveStream` sends each viewer only the resolution its tile is drawn at,
 * and pauses video nobody is looking at; `dynacast` stops publishing layers no
 * one subscribes to. Together they are what keeps a group session, where
 * everyone watches everyone, from costing many times a one-to-one.
 */
export const MEETING_ROOM_OPTIONS: RoomOptions = {
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: {
        resolution: MEETING_CAMERA_PRESET.resolution,
    },
    publishDefaults: {
        videoEncoding: MEETING_CAMERA_PRESET.encoding,
        simulcast: true,
        // Not capped: a shared document or whiteboard is unreadable at 360p,
        // and screen sharing is occasional, so it is not what the bill is.
        screenShareEncoding: VideoPresets.h1080.encoding,
        // Audio is deliberately left at the SDK's defaults. It is what the
        // session's transcription, notes and clinical alerts are made from,
        // and it is a rounding error next to video. Do not tune it for cost.
    },
};
